import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO } from 'date-fns'
import { InfoLabel } from './grade-parts'
import { formatNumber } from './metrics'

export const OVERSPEED_KEY = 'overspeed'

export interface Observation {
  key: string
  value?: number | null
  unavailableReason?: string | null
}

export interface OverspeedEvent {
  datetimeStart: string
  datetimeEnd?: string | null
  vehicleVin: string
  vehicleRegistrationNumber?: string | null
  maxSpeed?: number | null
  authorisedSpeed?: number | null
}

const OBSERVATION_KEYS = [
  'over_efficient_speed_share',
  'over_limiter_share',
  'very_heavy_pedal_share',
  'firm_deceleration_share',
  'harsh_deceleration_share',
] as const
type ObservationKey = (typeof OBSERVATION_KEYS)[number]

const isObservationKey = (key: string): key is ObservationKey =>
  (OBSERVATION_KEYS as readonly string[]).includes(key)

const useTrialTexts = () =>
  useTranslate({
    title: 'driver-grade.trial-title',
    info: 'driver-grade.trial-info',
    label: {
      over_efficient_speed_share:
        'driver-grade.metric-over_efficient_speed_share',
      over_limiter_share: 'driver-grade.metric-over_limiter_share',
      very_heavy_pedal_share: 'driver-grade.metric-very_heavy_pedal_share',
      firm_deceleration_share: 'driver-grade.metric-firm_deceleration_share',
      harsh_deceleration_share: 'driver-grade.metric-harsh_deceleration_share',
      overspeed: 'driver-grade.metric-overspeed',
    },
    explanation: {
      over_efficient_speed_share:
        'driver-grade.info-over_efficient_speed_share',
      over_limiter_share: 'driver-grade.info-over_limiter_share',
      very_heavy_pedal_share: 'driver-grade.info-very_heavy_pedal_share',
      firm_deceleration_share: 'driver-grade.info-firm_deceleration_share',
      harsh_deceleration_share: 'driver-grade.info-harsh_deceleration_share',
      overspeed: 'driver-grade.info-overspeed',
    },
    unavailable: {
      unsupported_rfms_capability:
        'driver-grade.unavailable-unsupported_rfms_capability',
      missing_class_counters: 'driver-grade.unavailable-missing_class_counters',
      insufficient_class_coverage:
        'driver-grade.unavailable-insufficient_class_coverage',
      insufficient_highway_time:
        'driver-grade.unavailable-insufficient_highway_time',
      other: 'driver-grade.unavailable-other',
    },
    none: 'driver-grade.overspeed-none',
    max: ['driver-grade.overspeed-max', { speed: '' }],
    limit: ['driver-grade.overspeed-limit', { speed: '' }],
  })

const ObservationRow: React.FC<{ observation: Observation }> = ({
  observation,
}) => {
  const translations = useTrialTexts()
  if (!isObservationKey(observation.key)) return null

  const { value, unavailableReason } = observation
  const reason =
    translations.unavailable[
      unavailableReason as keyof typeof translations.unavailable
    ] ?? translations.unavailable.other

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2">
      <InfoLabel
        label={translations.label[observation.key]}
        info={translations.explanation[observation.key]}
      />
      {value === null || value === undefined ? (
        <span className="text-right text-xs text-stone-400">{reason}</span>
      ) : (
        <span className="text-sm font-semibold tabular-nums text-stone-900">
          {formatNumber(value, value < 1 ? 2 : 1)} %
        </span>
      )}
    </div>
  )
}

const eventTime = (event: OverspeedEvent) => {
  const start = format(parseISO(event.datetimeStart), 'HH:mm')
  return event.datetimeEnd
    ? `${start}–${format(parseISO(event.datetimeEnd), 'HH:mm')}`
    : start
}

export const TrialMeasures: React.FC<{
  observations: Observation[]
  overspeedEvents: OverspeedEvent[]
  active: boolean
  pinned: boolean
  pinnedEvent: number | null
  onHover(key: string | null): void
  onPin(key: string): void
  onSelectEvent(index: number | null): void
}> = ({
  observations,
  overspeedEvents,
  active,
  pinned,
  pinnedEvent,
  onHover,
  onPin,
  onSelectEvent,
}) => {
  const translations = useTrialTexts()

  return (
    <div className="space-y-2 px-3 py-3">
      <div className="px-2">
        <InfoLabel label={translations.title} info={translations.info} />
      </div>
      <div
        role="button"
        tabIndex={0}
        onMouseEnter={() => onHover(OVERSPEED_KEY)}
        onMouseLeave={() => onHover(null)}
        onClick={() => onPin(OVERSPEED_KEY)}
        onKeyDown={event => event.key === 'Enter' && onPin(OVERSPEED_KEY)}
        className={cn(
          'cursor-pointer space-y-2 rounded-xl border px-4 py-3 transition-colors',
          active
            ? 'border-stone-400 bg-stone-50'
            : 'border-transparent hover:bg-stone-50',
          pinned && 'border-stone-900'
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <InfoLabel
            label={translations.label.overspeed}
            info={translations.explanation.overspeed}
          />
          <span className="text-sm font-semibold tabular-nums text-stone-900">
            {overspeedEvents.length || translations.none}
          </span>
        </div>
        {overspeedEvents.length > 0 && (
          <ul className="space-y-1">
            {overspeedEvents.map((event, index) => (
              <li key={event.datetimeStart}>
                <button
                  type="button"
                  onClick={clickEvent => {
                    clickEvent.stopPropagation()
                    onSelectEvent(pinnedEvent === index ? null : index)
                  }}
                  className={cn(
                    'flex w-full flex-wrap gap-x-2 rounded-lg px-2 py-1 text-left text-xs tabular-nums text-stone-700 hover:bg-white',
                    pinnedEvent === index && 'bg-white ring-1 ring-stone-900'
                  )}
                >
                  <span className="font-semibold">{eventTime(event)}</span>
                  {event.maxSpeed != null && (
                    <span>
                      {translations.max({ speed: String(event.maxSpeed) })}
                    </span>
                  )}
                  {event.authorisedSpeed != null && (
                    <span className="text-stone-500">
                      {translations.limit({
                        speed: String(event.authorisedSpeed),
                      })}
                    </span>
                  )}
                  <span className="text-stone-500">
                    {event.vehicleRegistrationNumber || event.vehicleVin}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {observations.map(observation => (
        <ObservationRow key={observation.key} observation={observation} />
      ))}
    </div>
  )
}
